import { FC, useCallback, useState, ComponentProps } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import Router from "next/router";

import { FullWidthVStack } from "@components/common";
import Pagination from "@components/Pagination";
import useIsMounted from "@hooks/useIsMounted";
import { Desktop, Mobile } from "@lib/media";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { ROUTES } from "@utils/constants";

import type { TFaqPageListProps } from "../types";
import FaqList from "./FaqList";
import FaqSkeleton from "./FaqSkeleton";

const FaqPageList: FC<TFaqPageListProps> = ({ faq }) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const isMounted = useIsMounted();

  // TODO: maybe move click handlers, redirecting and loading state handling to usePagination hook

  // No need to hide loading, because after page loads current page is unmounted
  const handlePreviousPageClick = useCallback(async () => {
    if (!faq?.pagination) {
      return;
    }
    setIsLoading(true);
    await Router.push({ pathname: ROUTES.FAQ, query: { page: faq.pagination.currentPage - 1 } });
    if (isMounted()) {
      setIsLoading(false);
    }
  }, [faq, isMounted]);
  const handleNextPageClick = useCallback(async () => {
    if (!faq?.pagination) {
      return;
    }
    setIsLoading(true);
    await Router.push({ pathname: ROUTES.FAQ, query: { page: faq.pagination.currentPage + 1 } });
    if (isMounted()) {
      setIsLoading(false);
    }
  }, [faq, isMounted]);

  if (!faq?.items?.length || faq?.pagination?.totalCount === 0) {
    return (
      <FullWidthVStack pt="1rem" pb="2rem" px={responsiveStyle({ mobile: "1rem", desktop: "0" })}>
        <Pagination hasTotalCount totalCount={0} />
        <Box fontSize="1.125rem">{t("faq-page-no_items")}</Box>
      </FullWidthVStack>
    );
  }

  const paginationProps: ComponentProps<typeof Pagination> = {
    currentPage: faq.pagination.currentPage,
    totalCount: faq.pagination.totalCount,
    pageSize: faq.pagination.itemSize,
    onPreviousPageClick: handlePreviousPageClick,
    onNextPageClick: handleNextPageClick,
    isShowingLowTotalCount: true,
  };

  return (
    <FullWidthVStack
      pt="1rem"
      pb="2rem"
      spacing="1.5rem"
      px={responsiveStyle({ mobile: "1rem", desktop: "0" })}
      flex="1"
    >
      <Desktop>
        <Pagination
          hasTotalCount
          hasPageSwitcher={faq.pagination.totalCount > faq.pagination.itemSize}
          {...paginationProps}
        />
      </Desktop>
      <Mobile>
        <Pagination hasTotalCount {...paginationProps} />
      </Mobile>

      <Box flex="1">
        <FaqSkeleton isLoading={isLoading}>
          <FaqList faq={faq.items} isFaqPage />
        </FaqSkeleton>
      </Box>

      {faq.pagination.totalCount > faq.pagination.itemSize && (
        <>
          <Desktop>
            <Pagination hasPageSwitcher {...paginationProps} />
          </Desktop>
          <Mobile>
            <Pagination hasPageSwitcher isCentered {...paginationProps} />
          </Mobile>
        </>
      )}
    </FullWidthVStack>
  );
};
export default FaqPageList;
